import {
  Button,
  Pencil,
  Plus,
  RefreshCw,
  ToggleRight,
  Trash2,
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ToggleSwitch } from "../../components/atoms/ToggleSwitch";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearError,
  createFeatureFlagRequest,
  deleteFeatureFlagRequest,
  fetchFeatureFlagsRequest,
  selectFeatureFlagDeleting,
  selectFeatureFlagLoading,
  selectFeatureFlagRules,
  selectFeatureFlagSaving,
  updateFeatureFlagRequest,
} from "../../store/reducers/featureFlagSlice";
import {
  FeatureFlagDto,
  FeatureFlagInput,
  toPayload,
} from "../../types/featureFlag.definition";
import { appNoticeScopeLabel } from "../../utils/enum-label-utils";
import { FeatureFlagRuleModal } from "./FeatureFlagRuleModal";

const DUPLICATE_ERROR_CODE = "FEATURE_FLAG_DUPLICATE_RULE";

export const FeatureFlagConfig: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const rules = useAppSelector(selectFeatureFlagRules);
  const isLoading = useAppSelector(selectFeatureFlagLoading);
  const isSaving = useAppSelector(selectFeatureFlagSaving);
  const isDeleting = useAppSelector(selectFeatureFlagDeleting);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<FeatureFlagDto | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchFeatureFlagsRequest());
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleRefresh = () => dispatch(fetchFeatureFlagsRequest());

  const openCreate = () => {
    setEditingRule(null);
    setInlineError(null);
    setIsFormOpen(true);
  };

  const openEdit = (rule: FeatureFlagDto) => {
    setEditingRule(rule);
    setInlineError(null);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingRule(null);
    setInlineError(null);
  };

  const handleSubmit = (input: FeatureFlagInput) => {
    setInlineError(null);
    const payload = toPayload(input);

    const onError = (code: string) => {
      if (code === DUPLICATE_ERROR_CODE) {
        setInlineError(t("admin.governance.featureFlag.duplicateError"));
      } else {
        toast.error(t("admin.governance.featureFlag.toast.saveError"));
      }
    };

    if (editingRule) {
      dispatch(
        updateFeatureFlagRequest({
          id: editingRule.id,
          data: payload,
          onSuccess: () => {
            closeForm();
            toast.success(t("admin.governance.featureFlag.toast.updateOk"));
          },
          onError,
        })
      );
    } else {
      dispatch(
        createFeatureFlagRequest({
          data: payload,
          onSuccess: () => {
            closeForm();
            toast.success(t("admin.governance.featureFlag.toast.createOk"));
          },
          onError,
        })
      );
    }
  };

  const handleToggle = (rule: FeatureFlagDto) => {
    setTogglingId(rule.id);
    const payload = toPayload({
      flagName: rule.flagName,
      environment: rule.environment,
      country: rule.country,
      enabled: !rule.enabled,
    });
    dispatch(
      updateFeatureFlagRequest({
        id: rule.id,
        data: payload,
        onSuccess: () => {
          setTogglingId(null);
          toast.success(t("admin.governance.featureFlag.toast.updateOk"));
        },
        onError: () => {
          setTogglingId(null);
          toast.error(t("admin.governance.featureFlag.toast.saveError"));
        },
      })
    );
  };

  const handleDelete = () => {
    if (deletingId === null) return;
    const id = deletingId;
    setDeletingId(null);
    dispatch(
      deleteFeatureFlagRequest({
        id,
        onSuccess: () => {
          toast.success(t("admin.governance.featureFlag.toast.deleteOk"));
        },
        onError: () => {
          toast.error(t("admin.governance.featureFlag.toast.deleteError"));
        },
      })
    );
  };

  // MED-1543: this list printed the raw stored scope ("local") while the
  // announcement list next to it showed the label ("Local") — MED-1516 fixed
  // it there and left this one behind. Same mapping, one source.
  const renderScope = (
    kind: "environments" | "countries",
    value: string | null
  ) =>
    value === null ? (
      <span className="text-neutral-500 italic">
        {t("admin.governance.scope.all")}
      </span>
    ) : (
      <span className="text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">
        {appNoticeScopeLabel(t, kind, value)}
      </span>
    );

  return (
    <div className="page-container">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t("admin.governance.featureFlag.title")}
          </h2>
          <p className="text-neutral-600">
            {t("admin.governance.featureFlag.subtitle")}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center space-x-2"
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span>{t("common.refresh")}</span>
          </Button>
          <Button onClick={openCreate} className="flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>{t("admin.governance.featureFlag.newRule")}</span>
          </Button>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <div className="page-section">
        {isLoading ? (
          <div className="py-12 text-center">
            <RefreshCw className="mx-auto w-8 h-8 animate-spin text-neutral-400" />
            <p className="mt-2 text-sm text-neutral-500">
              {t("common.loading")}
            </p>
          </div>
        ) : rules.length === 0 ? (
          <div className="py-12 text-center">
            <ToggleRight className="mx-auto w-12 h-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("admin.governance.featureFlag.empty")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t("admin.governance.featureFlag.emptyHint")}
            </p>
            <div className="mt-6">
              <Button onClick={openCreate}>
                <Plus className="mr-2 w-4 h-4" />
                {t("admin.governance.featureFlag.newRule")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-neutral-200">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.governance.featureFlag.table.flagName")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.governance.table.environment")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.governance.table.country")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.governance.table.enabled")}
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-neutral-600 w-32">
                    {t("common.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {rules.map(rule => (
                  <tr
                    key={rule.id}
                    className="hover:bg-neutral-50 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-neutral-800">
                      {t(`admin.governance.featureFlag.names.${rule.flagName}`)}
                    </td>
                    <td className="px-4 py-3">
                      {renderScope("environments", rule.environment)}
                    </td>
                    <td className="px-4 py-3">
                      {renderScope("countries", rule.country)}
                    </td>
                    <td className="px-4 py-3">
                      <ToggleSwitch
                        checked={rule.enabled}
                        onChange={() => handleToggle(rule)}
                        disabled={togglingId === rule.id || isSaving}
                        ariaLabel={t("admin.governance.featureFlag.toggle", {
                          flag: rule.flagName,
                        })}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end items-center space-x-1">
                        <button
                          onClick={() => openEdit(rule)}
                          className="p-1.5 rounded text-neutral-600 hover:bg-neutral-100 hover:text-ews-primary transition-colors"
                          title={t("common.edit")}
                          aria-label={t("common.edit")}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingId(rule.id)}
                          className="p-1.5 rounded text-neutral-600 hover:bg-danger-50 hover:text-danger-600 transition-colors"
                          title={t("common.delete")}
                          aria-label={t("common.delete")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <FeatureFlagRuleModal
        isOpen={isFormOpen}
        onClose={closeForm}
        onSubmit={handleSubmit}
        initial={editingRule}
        isSaving={isSaving}
        inlineError={inlineError}
      />

      <ConfirmationModal
        isOpen={deletingId !== null}
        onClose={() => setDeletingId(null)}
        onConfirm={handleDelete}
        title={t("admin.governance.featureFlag.deleteConfirm.title")}
        message={t("admin.governance.featureFlag.deleteConfirm.message")}
        variant="danger"
        isLoading={isDeleting}
        confirmLabel={t("common.delete")}
      />
    </div>
  );
};
